import { MESSAGE_TYPES } from './MessageTypes.js';
import { IMessageSender } from './IMessageHandler.js';
import { logger } from '@/utils/logger';
import { STORAGE_KEYS } from '@/constants/constants';

/**
 * 消息发送器
 * 负责向iframe发送各种类型的消息
 */
export class MessageSender extends IMessageSender {
  /**
   * 发送消息到iframe
   * @param {Object} messageData - 消息数据
   * @param {HTMLIFrameElement} iframe - iframe元素
   */
  sendMessageToIframe(messageData, iframe) {
    if (!iframe || !iframe.contentWindow) {
      throw new Error('iframe不存在或未加载完成');
    }

    try {
      iframe.contentWindow.postMessage(messageData, '*');
      logger.debug('[MessageSender] 发送消息到iframe:', messageData);
    } catch (error) {
      logger.error('[MessageSender] 发送消息失败:', error);
      throw error;
    }
  }

  /**
   * 发送初始化聊天消息
   * @param {HTMLIFrameElement} iframe - iframe元素
   * @param {Object} data - 初始化数据
   */
  sendInitMessage(iframe, data) {
    // 获取租户ID
    const erpUserInfoStr = localStorage.getItem(STORAGE_KEYS.ERP_USER_INFO);
    let tenantId = null;

    if (erpUserInfoStr) {
      try {
        const erpUserInfo = JSON.parse(erpUserInfoStr);
        tenantId = erpUserInfo.tenantId;
      } catch (error) {
        logger.error('[MessageSender] 解析 ERP 用户信息失败:', error);
      }
    }

    // 构建 INIT_CHAT 消息格式，参考稳定版本的实现
    const messageData = {
      type: MESSAGE_TYPES.INIT_CHAT,
      data: {
        sdkAppId: data.credentials?.sdkAppId,
        userId: data.credentials?.userId,
        userSig: data.credentials?.userSig,
        conversationId: data.conversationId,
        groupOwnerId: data.credentials?.groupOwnerId,
        tenantId: data.tenantId || tenantId,
        timestamp: Date.now(),
      },
    };

    // 添加 console.log 打印发送的消息数据
    console.log('[IFRAME] 发送到子窗体的 INIT_CHAT 消息数据:', {
      type: messageData.type,
      data: {
        sdkAppId: messageData.data.sdkAppId,
        userId: messageData.data.userId,
        hasUserSig: !!messageData.data.userSig,
        conversationId: messageData.data.conversationId,
        groupOwnerId: messageData.data.groupOwnerId,
        tenantId: messageData.data.tenantId,
        timestamp: messageData.data.timestamp,
      },
    });

    logger.info('[MessageSender] 发送 INIT_CHAT 消息:', {
      sdkAppId: messageData.data.sdkAppId,
      userId: messageData.data.userId,
      hasUserSig: !!messageData.data.userSig,
      conversationId: messageData.data.conversationId,
      groupOwnerId: messageData.data.groupOwnerId,
      tenantId: messageData.data.tenantId,
    });

    this.sendMessageToIframe(messageData, iframe);
  }

  /**
   * 发送注销消息
   * @param {HTMLIFrameElement} iframe - iframe元素
   * @param {string} reason - 注销原因
   */
  sendLogoutMessage(iframe, reason = 'manual') {
    const messageData = {
      type: MESSAGE_TYPES.LOGOUT,
      data: {
        reason,
        timestamp: Date.now(),
      },
    };

    this.sendMessageToIframe(messageData, iframe);
  }

  /**
   * 发送切换会话消息
   * @param {HTMLIFrameElement} iframe - iframe元素
   * @param {string} conversationId - 会话ID
   * @param {string} serviceUserId - 服务用户ID
   * @param {Object} credentials - 登录凭证（可选）
   * @param {string} groupOwnerId - 群组所有者ID
   */
  sendSwitchConversationMessage(
    iframe,
    conversationId,
    serviceUserId,
    credentials = null,
    groupOwnerId = null
  ) {
    const messageData = {
      type: MESSAGE_TYPES.SWITCH_CONVERSATION,
      data: {
        conversationId,
        serviceUserId,
        credentials, // 传递登录凭证
        groupOwnerId, // 传递群主ID
        timestamp: Date.now(),
      },
    };

    this.sendMessageToIframe(messageData, iframe);
  }
}
